import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, ExternalLink, Send } from 'lucide-react';
import { garageAPI } from '../../../services/garage/api';
import { formatApiErrorDetail, parseRepoUrlGateDetail } from '../../../utils/repoUrlGate';
import RepoUrlGateModal from './RepoUrlGateModal';
import '../AIGarage.css';

const RouteToITModal = ({ isOpen, idea, adminEmail, adminName, onClose, onSuccess }) => {
  const [notes, setNotes] = useState('');
  const [nonRepoRequest, setNonRepoRequest] = useState(false);
  const [repoUrlOverride, setRepoUrlOverride] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [mondayUrl, setMondayUrl] = useState(null);
  const [mondayCreated, setMondayCreated] = useState(true);
  const [repoGate, setRepoGate] = useState(null);
  const [repoGateBusy, setRepoGateBusy] = useState(false);

  useEffect(() => {
    if (!isOpen || !idea) return;
    setNonRepoRequest(Boolean(idea.itScansNotApplicable));
    setRepoUrlOverride('');
    setNotes('');
    setError(null);
    setSuccess(null);
    setMondayUrl(null);
    setMondayCreated(true);
    setRepoGate(null);
  }, [isOpen, idea]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !submitting && !repoGate) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, submitting, repoGate]);

  const submitRoute = async ({ acknowledgeExistingGarageCopy = false } = {}) => {
    const url = (repoUrlOverride || idea?.repoUrl || '').trim();
    if (!nonRepoRequest) {
      if (url && !url.startsWith('https://')) {
        setError('Repository URL must be an https:// git URL.');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        adminEmail,
        adminName,
        notes: notes.trim() || undefined,
        nonRepoRequest: Boolean(nonRepoRequest),
        acknowledgeExistingGarageCopy: Boolean(acknowledgeExistingGarageCopy),
      };
      if (!nonRepoRequest && url) {
        payload.repoUrl = url;
        payload.originRef = (idea?.originRef || 'master').trim() || 'master';
      }

      const result = await garageAPI.routeToIT(idea.id, payload);
      setRepoGate(null);

      if (result.success) {
        setSuccess(true);
        setMondayUrl(result.mondayItemUrl);
        setMondayCreated(result.mondayCreated !== false);
        setTimeout(() => {
          onSuccess && onSuccess();
          onClose();
        }, 2000);
      } else {
        throw new Error(result.error || 'Failed to route to IT');
      }
    } catch (err) {
      console.error('Failed to route to IT:', err);
      const gate = parseRepoUrlGateDetail(err);
      if (gate) {
        setRepoGate(gate);
        return;
      }
      setError(formatApiErrorDetail(err, 'Failed to route to IT. Please try again.'));
    } finally {
      setSubmitting(false);
      setRepoGateBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitRoute();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="garage-modal-overlay" onClick={!submitting ? onClose : undefined}>
      <div className="garage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="garage-modal-header">
          <h2>Route to IT Team</h2>
          <button
            className="garage-modal-close"
            onClick={onClose}
            disabled={submitting}
          >
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body">
          {!success ? (
            <>
              <div className="garage-request-info-context">
                <p><strong>Request:</strong> {idea.title}</p>
                <p><strong>From:</strong> {idea.requesterName || idea.requesterEmail}</p>
                {idea.description && (
                  <div className="garage-route-description">
                    <strong>Description:</strong>
                    <p>{idea.description}</p>
                  </div>
                )}
              </div>

              <div className="garage-route-info-box">
                <p>
                  This creates a Monday.com IT ticket and moves the request to IT review.
                  Repository URL and access token are not collected here. If they are
                  missing, the requestor is asked to provide them. Scans start when an
                  admin clicks <strong>Initiate scans</strong> (unless this is a non-repo request).
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="garage-form-group">
                  <label className="garage-checkbox-label">
                    <input
                      type="checkbox"
                      checked={nonRepoRequest}
                      onChange={(e) => setNonRepoRequest(e.target.checked)}
                      disabled={submitting}
                    />
                    {' '}
                    Non-repo / packaged request (scans not applicable)
                  </label>
                  <p className="garage-field-hint" style={{ marginTop: '0.35rem' }}>
                    Skip Security and Code Review. Deploy and Release stay available.
                  </p>
                </div>

                <div className="garage-form-group">
                  <label htmlFor="routing-notes">
                    Notes for IT Team <span className="garage-optional">(Optional)</span>
                  </label>
                  <textarea
                    id="routing-notes"
                    className="garage-textarea"
                    rows="4"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any additional context or instructions for the IT team..."
                    disabled={submitting}
                  />
                </div>

                {error && (
                  <div className="garage-error-message">
                    {typeof error === 'string' ? error : JSON.stringify(error)}
                  </div>
                )}

                <div className="garage-modal-actions">
                  <button
                    type="button"
                    className="garage-btn garage-btn-secondary"
                    onClick={onClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="garage-btn garage-btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>Creating Ticket...</>
                    ) : (
                      <>
                        <Send size={16} style={{ marginRight: '6px' }} />
                        Route to IT
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="garage-success-state">
              <div className="garage-success-icon">✅</div>
              <h3>Successfully Routed to IT!</h3>
              <p>
                {mondayCreated
                  ? 'Monday.com peer ticket has been created.'
                  : 'Updated the existing Monday.com peer ticket (no duplicate).'}
              </p>
              {mondayUrl && (
                <a
                  href={mondayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="garage-monday-link"
                >
                  View Ticket <ExternalLink size={14} />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      <RepoUrlGateModal
        isOpen={Boolean(repoGate)}
        gate={repoGate}
        acknowledging={repoGateBusy}
        onCancel={() => setRepoGate(null)}
        onUseCopyUrl={(url) => {
          if (url) setRepoUrlOverride(url);
          setRepoGate(null);
        }}
        onAcknowledge={async () => {
          setRepoGateBusy(true);
          await submitRoute({ acknowledgeExistingGarageCopy: true });
        }}
      />
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default RouteToITModal;
